import {
  appFormsTreeAreaNodeMapExtended,
  appFormsTreeAreaNodesExtended,
  appFormsTreeFieldOptionsMapExtended,
  AREA_ID_PREFIX,
} from 'app/services/autoLockForms/autoLockForms';
import { AutoLockFormsLevelType } from 'app/services/autoLockForms/autoLockForms.constants';
import { normalizeLegacyAppFormsTreeId } from 'app/services/autoLockForms/autoLockForms.utils';
import {
  EDITOR_LINK_TARGETS,
  LOAN_UNREACHABLE_SECTIONS,
} from 'app/services/loanNavigation/loanNavigation.constants';
import _ from 'lodash';

// A checklist item's `fieldRef` is an opaque string. It links to EITHER a single
// loan-editor FIELD (the prefix-less full path `section-subsection-tab-form-leaf`,
// DEV-4383) OR a SECTION/area (the prefix-discriminated area id `subsection:...`,
// `section:...`, `tab:...`, `page:...` — DEV-4467). The two are told apart purely
// by the area-id prefix.
export const FieldLinkRefKind = {
  FIELD: 'field',
  SECTION: 'section',
};

// Only the prefixed area levels are offered as section links; the prefix-less FORM
// id is excluded because it is shaped exactly like a field ref and would be
// indistinguishable from one.
const SECTION_LEVEL_TYPES = [
  AutoLockFormsLevelType.PAGE,
  AutoLockFormsLevelType.SECTION,
  AutoLockFormsLevelType.SUBSECTION,
  AutoLockFormsLevelType.TAB,
];

const SECTION_REF_PREFIXES = Object.values(AREA_ID_PREFIX);

const SECTION_GROUP_LABEL = 'Sections & Areas';

/**
 * Discriminates a stored `fieldRef` by its shape: a prefixed area id is a SECTION,
 * anything else (incl. the empty/absent case) is a FIELD.
 *
 * @param {string} ref
 * @returns {('field'|'section')}
 */
export const getRefKind = ref =>
  _.isString(ref) && SECTION_REF_PREFIXES.some(prefix => ref.startsWith(prefix))
    ? FieldLinkRefKind.SECTION
    : FieldLinkRefKind.FIELD;

// Build a readable breadcrumb for an area node from the labels valid at/above its
// level. (A subsection node's `tabLabel` can be stale carry-over from a sibling's
// tab, so it is only read at TAB level.)
const LEVEL_LABEL_CHAIN = {
  [AutoLockFormsLevelType.PAGE]: node => [node.pageLabel],
  [AutoLockFormsLevelType.SECTION]: node => [node.sectionLabel],
  [AutoLockFormsLevelType.SUBSECTION]: node => [node.sectionLabel, node.subsectionLabel],
  [AutoLockFormsLevelType.TAB]: node => [node.sectionLabel, node.subsectionLabel, node.tabLabel],
};

const buildSectionLabel = node =>
  (LEVEL_LABEL_CHAIN[node.levelType]?.(node) || [node.label]).filter(Boolean).join(' / ');

const buildSectionOption = node => ({
  name: buildSectionLabel(node),
  value: node.id,
  kind: FieldLinkRefKind.SECTION,
  groupLabel: SECTION_GROUP_LABEL,
});

// Editor screens with no tree area (e.g. Programs & Pricing, LEN2-499) are offered as
// section links too, labelled under their parent area's breadcrumb.
const buildEditorLinkTargetOption = ([ref, { label, parentAreaId }]) => ({
  name: [buildSectionLabel(appFormsTreeAreaNodeMapExtended[parentAreaId]), label].join(' / '),
  value: ref,
  kind: FieldLinkRefKind.SECTION,
  groupLabel: SECTION_GROUP_LABEL,
});

// Lead details have no loan screen to go to, so they are not offered as links (LEN2-499).
const isLinkableArea = ({ section }) => !LOAN_UNREACHABLE_SECTIONS.includes(section);

const sectionLinkOptions = [
  ...appFormsTreeAreaNodesExtended
    .filter(node => SECTION_LEVEL_TYPES.includes(node.levelType) && isLinkableArea(node))
    .map(buildSectionOption),
  ...Object.entries(EDITOR_LINK_TARGETS).map(buildEditorLinkTargetOption),
];

// Resolves a stored section `fieldRef` back to its picker option (mirrors how
// `appFormsTreeFieldOptionsMapExtended` resolves a stored field ref).
export const sectionLinkOptionsMap = Object.fromEntries(
  sectionLinkOptions.map(option => [option.value, option]),
);

// The field options collapse 5-part composite-id collisions to one entry per
// unique value (see DEV-4383); sections are appended as a distinct group.
const fieldOnlyOptions = Object.values(appFormsTreeFieldOptionsMapExtended).filter(isLinkableArea);

export const fieldLinkOptions = [...fieldOnlyOptions, ...sectionLinkOptions];

/**
 * Resolves a stored `fieldRef` back to its picker option — a section option for an area
 * ref, the tree's field option otherwise. Ids stored before the Lead/Activities/Tasks
 * section names were restored resolve too (LEN2-499).
 *
 * @param {string} fieldRef
 * @returns {Object|null}
 */
export const getFieldLinkOption = fieldRef => {
  if (!fieldRef) {
    return null;
  }

  const ref = normalizeLegacyAppFormsTreeId(fieldRef);

  return (
    (getRefKind(ref) === FieldLinkRefKind.SECTION
      ? sectionLinkOptionsMap[ref]
      : appFormsTreeFieldOptionsMapExtended[ref]) ?? null
  );
};

/**
 * @param {Object} option - a field option or a section option
 * @returns {string}
 */
export const getFieldGroup = option =>
  option.groupLabel || `${option.sectionLabel} / ${option.tabLabel}`;

/**
 * @param {Object} option
 * @param {Object} val
 * @returns {boolean}
 */
export const isFieldOptionEqualToValue = (option, val) => option.value === val.value;
